import { create } from 'zustand';

import { type Config, defaultConfig } from '../lib/config';
import { type GenericElement } from '../lib/element';
import { type Plugin } from '../lib/plugin';
import { type Zoom } from '../lib/zoom';
import { type KeyPress } from '../utils/hooks/useKeyPress';
import { type Point } from '../utils/math';

import { type ElementMap } from './useEditorStore';

export type EditorNotification = {
  type: 'warning' | 'info';
  reason: 'pathMinPoints';
};

export interface RuntimeState {
  activeKeyPress?: KeyPress;
  activeTool: string;
  activeToolSubMode: string;
  activeTransformation?: {
    hasChanges: boolean;
    origin: Point;
    originalElements: ElementMap;
    type: string;
  };
  config: Config;
  currentHistoryIndex: number;
  interactiveCanvas: HTMLCanvasElement | null;
  lastActiveTool?: string;
  lastActiveToolSubMode?: string;
  newCanvas: HTMLCanvasElement | null;
  newElement?: GenericElement;
  offsetLeft: number;
  offsetTop: number;
  originalNewElement?: GenericElement;
  plugins: Map<string, Plugin>;
  scrollX: number;
  scrollY: number;
  selectionElement?: GenericElement;
  limitSelectionToIds: string[];
  panConstraintElementId?: string;
  readonly: boolean;
  shouldCacheIgnoreZoom: boolean;
  staticCanvas: HTMLCanvasElement | null;
  zoom: Zoom;
  onElementEvent?: (
    type: 'created' | 'updated' | 'deleted',
    element: GenericElement,
  ) => void;
  onEditorNotification?: (notification: EditorNotification) => void;
}

export const initialRuntimeStoreState: RuntimeState = {
  activeKeyPress: undefined,
  activeTool: 'selection',
  activeToolSubMode: 'default',
  activeTransformation: undefined,
  config: defaultConfig,
  currentHistoryIndex: 0,
  interactiveCanvas: null,
  lastActiveTool: undefined,
  lastActiveToolSubMode: undefined,
  newCanvas: null,
  newElement: undefined,
  offsetLeft: 0,
  offsetTop: 0,
  originalNewElement: undefined,
  plugins: new Map(),
  scrollX: 0,
  scrollY: 0,
  selectionElement: undefined,
  readonly: false,
  shouldCacheIgnoreZoom: false,
  staticCanvas: null,
  zoom: { value: 1 },
  onElementEvent: undefined,
  onEditorNotification: undefined,
  limitSelectionToIds: [],
  panConstraintElementId: undefined,
};

export const useEditorRuntimeStore = create<RuntimeState>()(() => ({
  ...initialRuntimeStoreState,
}));

export const getConfig = () => {
  return useEditorRuntimeStore.getState().config;
};

export const updateRuntimeStore = (state: Partial<RuntimeState>) => {
  useEditorRuntimeStore.setState(state);
  return useEditorRuntimeStore.getState();
};
